import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonCard,
    IonCardContent,
    IonGrid,
    IonRow,
    IonCol,
    IonText,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}

const shopNames = [
    'Toy Store', 'Stationery', 'Ice Cream', 'Book Shop',
    'Grocery', 'Chemist', 'Fruit Shop', 'Bakery', 'Bank'
];



const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;


const GeometryMaps: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedType, setSelectedType] = useState(0);


    const [mapGrid, setMapGrid] = useState<string[][]>([]);

    const getRandomFromArray = (arr: string[], count: number) => {
        const shuffled = [...arr].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, count);
    };

    const generateMapLayout = () => {
        const shuffled = [...shopNames].sort(() => 0.5 - Math.random());
        const grid: string[][] = [];
        for (let i = 0; i < 3; i++) {
            grid.push(shuffled.slice(i * 3, i * 3 + 3));
        }
        return grid;
    };

    const generateQuestions = (grid: string[][]): Question[] => {
        const questions: Question[] = [];
        const directions = ['left', 'right', 'above', 'below'];

        const isValid = (row: number, col: number) =>
            row >= 0 && row < 3 && col >= 0 && col < 3;

        const directionOffset: Record<string, [number, number]> = {
            left: [0, -1],
            right: [0, 1],
            above: [-1, 0],
            below: [1, 0],
        };

        if (grid) {

            questions.push({
                question: `If someone enters the market from Gate 1, Which shop will be to his/her left?`,
                options: [grid[2][0], grid[1][1], grid[0][0], grid[1][0]],
                answer: grid[0][0],
                type: 'multiple-choice',
            });

            questions.push({
                question: `If someone enters the market from Gate 1, Which shop will be to his/her right?`,
                options: [grid[0][2], grid[1][0], grid[0][0], grid[0][1]],
                answer: grid[1][0],
                type: 'multiple-choice',
            });

            questions.push({
                question: `If someone enters the market from Gate 2, Which shop will be to his/her left?`,
                options: [grid[0][1], grid[2][2], grid[1][2], grid[1][0]],
                answer: grid[2][2],
                type: 'multiple-choice',
            });

            questions.push({
                question: `If someone enters the market from Gate 2, Which shop will be to his/her right?`,
                options: [grid[2][2], grid[2][1], grid[2][0], grid[1][2]],
                answer: grid[1][2],
                type: 'multiple-choice',
            });
        }

        for (let i = 0; i < 5; i++) {
            const row = Math.floor(Math.random() * 3);
            const col = Math.floor(Math.random() * 3);
            const direction = directions[Math.floor(Math.random() * directions.length)];
            const [dRow, dCol] = directionOffset[direction];

            const targetRow = row + dRow;
            const targetCol = col + dCol;

            if (isValid(targetRow, targetCol)) {
                const shop = grid[row][col];
                const answer = grid[targetRow][targetCol];
                const otherOptions = getRandomFromArray(
                    shopNames.filter((name) => name !== answer),
                    3
                );
                const options = [...otherOptions, answer].sort(() => 0.5 - Math.random());

                questions.push({
                    question: `Which shop is ${direction} of the ${shop}?`,
                    options,
                    answer,
                    type: 'multiple-choice',
                });
            }
        }

        return questions;
    };

    const createNewQuestion = async () => {
        const patternType = getRandomInt(0, 1);
        setSelectedType(patternType);

        let q: Question[] = [];
        if (patternType === 0) {
            await loadGeometryQuestions(); // this already sets state
        } else {

            const layout = generateMapLayout();
            setMapGrid(layout);
            q = shuffleArray(generateQuestions(layout));
            setQuestions(q);
            console.log(q);
            setCurrentQuestion(q[0])
        }
    };






    const loadGeometryQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeMathematics/GeometryMaps.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeMathematics/GeometryMaps.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions); 
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                setSelectedOption(null);
                createNewQuestion()
                // const nextIndex = currentIndex + 1;
                // if (nextIndex < questions.length) {
                //     setCurrentIndex(nextIndex);
                //     setShowImage(false);
                //     setSelectedOption(null);
                //     setCurrentQuestion(questions[nextIndex]);
                // }
                // else {
                //     setCurrentIndex(0);
                //     loadGeometryQuestions();
                // }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        createNewQuestion();

        document.title =
            "Mamas Guidance - Class Three - Mathematics";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 1 IGKO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {selectedType === 0 ? (
                                <>
                                    {(currentQuestion.type === "multiple-choice" || currentQuestion.type === "fill-in-the-blank") && (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            <IonList>
                                                {(currentQuestion.options || []).map((option, index) => (
                                                    <IonItem
                                                        key={index}
                                                        className={`grid-item ${selectedOption === option
                                                            ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                                ? "correct"
                                                                : "errorItems"
                                                            : ""
                                                            }`}
                                                    >
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}

                                    {currentQuestion.type === "true-false" && (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            <IonList>
                                                {["true", "false"].map((option, index) => (
                                                    <IonItem
                                                        key={index}
                                                        className={`${selectedOption === option
                                                            ? option.toLowerCase() === currentQuestion.answer
                                                                ? "correct"
                                                                : "errorItems"
                                                            : ""
                                                            }`}
                                                    >
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option.toUpperCase()}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}
                                </>
                            ) : (
                                <>
                                    {("options" in currentQuestion) && (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            <IonList>
                                                {(currentQuestion.options || []).map((option, index) => (
                                                    <IonItem
                                                        key={index}
                                                        className={`grid-item ${selectedOption === option
                                                            ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                                ? "correct"
                                                                : "errorItems"
                                                            : ""
                                                            }`}
                                                    >
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}
                                    <IonGrid>
                                        {mapGrid.map((row, rowIndex) => (
                                            <React.Fragment key={rowIndex}>
                                                <IonRow>
                                                    <IonCol className="road" size="1"></IonCol>
                                                    {row.map((shop, colIndex) => (
                                                        <React.Fragment key={colIndex}>
                                                            <IonCol className="shop" size="2">{shop}</IonCol>
                                                            {colIndex < 2 && (
                                                                <IonCol className="road" size="1"></IonCol>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                    <IonCol className="road" size="1"></IonCol>
                                                </IonRow>

                                                {rowIndex < 2 && (
                                                    <IonRow>
                                                        {rowIndex === 0 && (
                                                            <IonCol className="gate" size="1">🚪 1</IonCol>
                                                        )}
                                                        {rowIndex != 0 && (
                                                            <IonCol className="road" size="1"></IonCol>
                                                        )}
                                                        <IonCol className="road" size="2">----------</IonCol>
                                                        <IonCol className="road" size="1"></IonCol>
                                                        <IonCol className="road" size="2">----------</IonCol>
                                                        <IonCol className="road" size="1"></IonCol>
                                                        <IonCol className="road" size="2">----------</IonCol>
                                                        {rowIndex === 1 && (
                                                            <IonCol className="gate" size="1">🚪 2</IonCol>
                                                        )}
                                                        {rowIndex != 1 && (
                                                            <IonCol className="road" size="1"></IonCol>
                                                        )}
                                                    </IonRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </IonGrid>
                                </>
                            )}


                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default GeometryMaps;
